import { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  LinkIcon,
  LinkSlashIcon,
  QueueListIcon,
  ShieldCheckIcon,
  TrashIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type {
  AdminUser,
  CreateUserRequest,
  Grant,
  Locale,
  RequestLog,
  UserRole,
} from '@xphoto/shared';
import {
  ACCESS_GATEWAY_URL_MAX_LENGTH,
  REQUEST_LOG_DEFAULT_LIMIT,
  SMALL_IMAGE_UPSCALE_MAX_PERCENT,
  SMALL_IMAGE_UPSCALE_MIN_PERCENT,
  userRoleSchema,
} from '@xphoto/shared';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatDateTime } from '../viewer/format';
import { useAuth } from '../auth/AuthProvider';
import { useCanWrite } from '../auth/useCanWrite';
import {
  AdminApiError,
  createGrant,
  createUser,
  createUserConflictKind,
  deleteGrant,
  deleteUser,
  fetchGrants,
  fetchRequestLogs,
  fetchUsers,
  isCannotDeleteSelf,
  isConflict,
  purgeRequestLogs,
  updateUser,
} from './admin-api';
import { Portal } from '../components/Portal';
import { rovingTabIndex, useRovingTabs } from '../components/useRovingTabs';
import { PasswordInput } from '../components/PasswordInput';
import { ConfirmModal } from '../taxonomy/ConfirmModal';
import { LIBRARY_ID } from '../gallery/config';
import { SharesList, listAllShares } from '../share';
import { LlmConfigPage } from '../ai';
import { AiTracePage } from '../aitrace';
import {
  AppConfigApiError,
  DEFAULT_APP_NAME,
  isStaleUpdate,
  updateAccessGatewayUrl,
  updateAppName,
  updateSmallImageUpscalePercent,
  useAppConfig,
} from '../app-config';
import { useHealth } from '../lib/useHealth';
import { ResetLibraryModal } from './ResetLibraryModal';
import { SessionLinksSection } from './SessionLinksSection';
import { ApiKeysSection } from './ApiKeysSection';
import { FileErrorsSection } from './FileErrorsSection';
import { MissingFilesSection } from './MissingFilesSection';
import { TrashSection } from './TrashSection';
import { AuditLogSection } from './AuditLogSection';
import { UserSessionsPanel } from './UserSessionsPanel';
import { PurgeByDateControl } from './PurgeByDateControl';
import { DynamicFilterSelect, FilterSelect, type FilterOption } from './FilterSelect';
import type { RequestLogMethod, RequestLogStatusClass } from '@xphoto/shared';

/**
 * Écran d'ADMINISTRATION (T7b, XPHOTO-25 ; converti en PAGE en 0.18.0).
 *
 * N'est PLUS une modale : PAGE rendue dans la zone `main` sous l'en-tête persistant
 * (`AppChrome`) — le fil d'Ariane (« Accueil / Admin / … ») reste visible et pilote
 * le retour. Plus de `role="dialog"`, de verrou de défilement, de focus-trap ni de
 * fermeture Échap : la page défile via son propre conteneur `overflow-y-auto`.
 *
 * DEUX consoles partagent ce composant via la prop `scope` (cf. `AdminScope`) :
 * - `gallery` — Admin de GALERIE (`/admin/*`, superadmin, admin OU propriétaire) :
 *     ACCÈS (grants lecture seule — liste/création/révocation), ERREURS DE FICHIERS
 *     et POUBELLE (par bibliothèque).
 * - `system` — SUPERADMIN / SYSTÈME (`/superadmin/*`, superadmin uniquement) :
 *     COMPTES (liste/création/rôle/suppression), JOURNAUX (requêtes), CONFIGURATION
 *     LLM (fournisseur de vision — T5 lot A), JOURNAL DES APPELS IA (diagnostic),
 *     MAINTENANCE (« repartir de zéro » — a migré de `/admin` vers `/superadmin` en
 *     v0.121).
 *
 * L'ouverture/gating est porté par `AccountSwitcher` (entrées « Admin » et
 * « Superadmin ») et `AdminRoute` : ce composant suppose déjà que l'utilisateur a le
 * droit d'en voir au moins une section de la portée demandée.
 */
/**
 * Portée du panneau : deux consoles distinctes partageant ce composant.
 *  - `gallery` — Admin de GALERIE (`/admin/*`) : Accès, Erreurs de fichiers, Poubelle.
 *    Ouvert au superadmin, à l'admin (écriture par son rôle) ou au PROPRIÉTAIRE d'une
 *    bibliothèque.
 *  - `system` — SUPERADMIN / SYSTÈME (`/superadmin/*`) : Comptes, Journaux,
 *    Configuration LLM, Journal des appels IA, Maintenance (réinitialisation).
 *    Réservée au superadmin.
 */
export type AdminScope = 'gallery' | 'system';

interface AdminPanelProps {
  onClose: () => void;
  /** Console rendue : galerie (`/admin`) ou système (`/superadmin`). */
  scope: AdminScope;
  /**
   * Onglet actif imposé par la ROUTE. Optionnel : absent ⇒ le panneau gère son
   * onglet localement (tests directs). Un onglet non autorisé par le rôle OU hors
   * de la portée retombe sur le premier onglet visible de la portée.
   */
  tab?: Tab | undefined;
  /** Notifie la route d'un changement d'onglet (navigation). */
  onTabChange?: ((tab: Tab) => void) | undefined;
}

/** Clé i18n du libellé de rôle (réutilise la section `auth`). */
const ROLE_LABEL: Record<UserRole, MessageKey> = {
  superadmin: 'auth.roleSuperadmin',
  admin: 'auth.roleAdmin',
  user: 'auth.roleUser',
};

const ROLES: readonly UserRole[] = userRoleSchema.options;

type Tab =
  | 'general'
  | 'users'
  | 'grants'
  | 'logs'
  | 'audit'
  | 'fileErrors'
  | 'missingFiles'
  | 'trash'
  | 'shares'
  | 'llm'
  | 'aitrace'
  | 'sessionLinks'
  | 'apiKeys'
  | 'reset';

/** Définition d'un onglet : id, portée, rôle requis, libellé i18n, icône. */
interface TabDef {
  id: Tab;
  scope: AdminScope;
  /** Rôle requis : `superadmin` (isSuperadmin) ou `grants` (propriétaire OU superadmin). */
  requires: 'superadmin' | 'grants';
  labelKey: MessageKey;
  panelId: string;
  icon: React.ReactNode;
}

export function AdminPanel({
  onClose,
  scope,
  tab: controlledTab,
  onTabChange,
}: AdminPanelProps): React.JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();

  const isSuperadmin = user?.role === 'superadmin';
  const ownLibraryId = user?.libraryId ?? null;
  // Bibliothèque cible de la console GALERIE. Un admin (écriture globale) n'a pas
  // de bibliothèque propre (`libraryId === null`) : il opère sur la bibliothèque
  // de repli (`LIBRARY_ID`, vide → `null` pour rester en état « aucune sélection »).
  const libraryId = ownLibraryId ?? (LIBRARY_ID || null);
  // Onglets galerie (Accès / Erreurs / Poubelle) : réservés à l'ACCÈS EN ÉCRITURE.
  // ⚠ Cette ligne redupliquait `superadmin || admin || libraryId` — une TROISIÈME copie
  // de la formule d'écriture, celle-là même que le docstring de `useCanWrite` interdit
  // (audit A1/A4). Elle passe par le helper partagé : même valeur qu'avant, mais une
  // seule définition. Du coup la prop `canWrite` des sections cesse d'être une
  // tautologie (« la section est visible ») pour redevenir ce qu'elle prétend être
  // (« l'utilisateur peut écrire »), et resterait juste si les deux venaient à diverger.
  const canWrite = useCanWrite();
  const canSeeGrants = canWrite;
  const allows = (requires: 'superadmin' | 'grants'): boolean =>
    requires === 'superadmin' ? isSuperadmin : canSeeGrants;

  // Toutes les sections, chacune avec sa PORTÉE (galerie/système) et son rôle requis.
  // Le panneau ne montre que celles de la portée courante ET permises par le rôle.
  const ALL_TABS: readonly TabDef[] = [
    // Admin de GALERIE (`/admin`) — superadmin, admin (écriture par son rôle) OU
    // propriétaire d'une bibliothèque. La Maintenance a migré vers la portée système.
    {
      id: 'grants',
      scope: 'gallery',
      requires: 'grants',
      labelKey: 'admin.tabGrants',
      panelId: 'admin-grants',
      icon: <KeyIcon className="h-5 w-5" aria-hidden="true" />,
    },
    {
      id: 'fileErrors',
      scope: 'gallery',
      requires: 'grants',
      labelKey: 'admin.fileErrors.tab',
      panelId: 'admin-file-errors',
      icon: <ExclamationCircleIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // Fichiers absents (audit A5) : médias dont le fichier a disparu du disque. Placés
    // ENTRE « Erreurs de fichiers » et « Poubelle » — c'est l'ordre du parcours réel
    // (constater l'anomalie → mettre à la corbeille → purger).
    {
      id: 'missingFiles',
      scope: 'gallery',
      requires: 'grants',
      labelKey: 'admin.missingFiles.tab',
      panelId: 'admin-missing-files',
      icon: <LinkSlashIcon className="h-5 w-5" aria-hidden="true" />,
    },
    {
      id: 'trash',
      scope: 'gallery',
      requires: 'grants',
      labelKey: 'admin.trash.tab',
      panelId: 'admin-trash',
      icon: <TrashIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // Partages publics (XPHOTO-31) : voir et couper les liens de TOUT LE MONDE est un
    // pouvoir d'administration de galerie, au même titre que les accès juste au-dessus.
    {
      id: 'shares',
      scope: 'gallery',
      requires: 'grants',
      labelKey: 'share.adminTitle',
      panelId: 'admin-shares',
      icon: <LinkIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // SUPERADMIN / SYSTÈME (`/superadmin`) — réservé au superadmin.
    // Général (LOT 3) : nom du site. Placé EN PREMIER des onglets système.
    {
      id: 'general',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'admin.tabGeneral',
      panelId: 'admin-general',
      icon: <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />,
    },
    {
      id: 'users',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'admin.tabUsers',
      panelId: 'admin-users',
      icon: <UsersIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // Liens d'accès communautaires (Incrément A2) : ouverture de session par URL
    // pour un compte pré-existant lecteur/contributeur (jamais superadmin).
    {
      id: 'sessionLinks',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'sessionLinks.tab',
      panelId: 'admin-session-links',
      icon: <LinkIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // Clés d'API externes (T8) : gouvernance système, regroupée après les liens
    // d'accès. Création/révocation/suppression réservées au superadmin.
    {
      id: 'apiKeys',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'settings.apiKeys.title',
      panelId: 'admin-api-keys',
      icon: <KeyIcon className="h-5 w-5" aria-hidden="true" />,
    },
    {
      id: 'logs',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'admin.logs.tab',
      panelId: 'admin-logs',
      icon: <ClipboardDocumentListIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // Configuration LLM (T5 lot A) : config du fournisseur de vision (opération globale).
    // Journal d'AUDIT (audit B1) : actions MÉTIER et leur auteur. Placé JUSTE APRÈS
    // « Journaux » — les deux se consultent ensemble, mais ne disent pas la même chose
    // (celui-ci trace le QUOI et le QUI, l'autre le trafic HTTP).
    {
      id: 'audit',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'admin.audit.tab',
      panelId: 'admin-audit',
      icon: <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />,
    },
    {
      id: 'llm',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'llm.tab',
      panelId: 'admin-llm',
      icon: <CpuChipIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // Journal des appels IA : diagnostic (traces des appels au LLM de vision).
    {
      id: 'aitrace',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'aiTrace.tab',
      panelId: 'admin-aitrace',
      icon: <QueueListIcon className="h-5 w-5" aria-hidden="true" />,
    },
    // Maintenance (« repartir de zéro ») : console SUPERADMIN/SYSTÈME, action
    // destructive réservée au superadmin — placée en dernier onglet.
    {
      id: 'reset',
      scope: 'system',
      requires: 'superadmin',
      labelKey: 'admin.reset.tab',
      panelId: 'admin-reset',
      icon: <WrenchScrewdriverIcon className="h-5 w-5" aria-hidden="true" />,
    },
  ];

  const onTabsKeyDown = useRovingTabs();
  const tabs = ALL_TABS.filter((d) => d.scope === scope && allows(d.requires));
  // Titre/étiquette de la console selon la portée.
  const titleKey: MessageKey = scope === 'system' ? 'superadmin.title' : 'admin.title';

  // Onglet actif : soit imposé par la route (contrôlé), soit géré localement. Un
  // onglet contrôlé hors portée ou non permis retombe sur l'onglet PAR DÉFAUT.
  const visibleIds = tabs.map((d) => d.id);
  // Onglet d'atterrissage par portée : aligné sur `DEFAULT_SEGMENT` d'`AdminRoute`
  // (`/superadmin` → Comptes, `/admin` → Accès) — donc PAS forcément `tabs[0]`
  // (« Général » ouvre la liste des onglets système mais n'est pas l'atterrissage).
  const DEFAULT_TAB: Record<AdminScope, Tab> = { system: 'users', gallery: 'grants' };
  const preferred = DEFAULT_TAB[scope];
  const fallbackTab: Tab = visibleIds.includes(preferred) ? preferred : (tabs[0]?.id ?? preferred);
  const [localTab, setLocalTab] = useState<Tab>(fallbackTab);
  const activeTab =
    controlledTab !== undefined && visibleIds.includes(controlledTab) ? controlledTab : undefined;
  const tab = activeTab ?? (controlledTab !== undefined ? fallbackTab : localTab);
  const setTab = (next: Tab): void => {
    if (onTabChange) onTabChange(next);
    else setLocalTab(next);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* En-tête de page. */}
      <div className="flex items-center justify-between gap-2 border-b border-border pb-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-fg">
          <Cog6ToothIcon className="h-6 w-6" aria-hidden="true" />
          {t(titleKey)}
        </h1>
        <button
          type="button"
          onClick={onClose}
          title={t('admin.close')}
          aria-label={t('admin.close')}
          className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Onglets (seulement s'il y a plus d'une section visible).
          Jusqu'à 14 onglets « icône + libellé » : sans `overflow-x-auto`, le
          `flex-shrink` par défaut les réduisait à quelques pixels sous 400 px de
          large. Même patron que les tableaux de ce fichier. */}
      {tabs.length > 1 && (
        <div
          role="tablist"
          aria-label={t(titleKey)}
          onKeyDown={onTabsKeyDown}
          className="flex gap-1 overflow-x-auto border-b border-border"
        >
          {tabs.map((def) => (
            <TabButton
              key={def.id}
              active={tab === def.id}
              onClick={() => setTab(def.id)}
              labelKey={def.labelKey}
              panelId={def.panelId}
            >
              {def.icon}
            </TabButton>
          ))}
        </div>
      )}

      {/*
       * Corps défilant (le seul conteneur de défilement de la page). `relative` :
       * ancre les descendants `sr-only`/`absolute` (ex. StatusBadge, labels masqués)
       * à CE conteneur au lieu du document → pas de 2e barre de défilement fantôme
       * (même cause qu'en v0.48.0 pour `/outils/dedup`). Défense en profondeur : le
       * `<main>` de la coquille (`AppShell`) porte déjà `relative`.
       */}
      <div className="relative min-h-0 flex-1 overflow-y-auto py-4">
        <div className="mx-auto flex max-w-4xl flex-col gap-8">
          {isSuperadmin && tab === 'general' && <GeneralSettingsSection />}
          {isSuperadmin && tab === 'users' && <UsersSection />}
          {tab === 'grants' && canSeeGrants && (
            <GrantsSection libraryId={libraryId} isSuperadmin={isSuperadmin} />
          )}
          {tab === 'fileErrors' && canSeeGrants && (
            <FileErrorsSection libraryId={libraryId} canWrite={canWrite} />
          )}
          {tab === 'missingFiles' && canSeeGrants && (
            <MissingFilesSection libraryId={libraryId} canWrite={canWrite} />
          )}
          {tab === 'trash' && canSeeGrants && (
            <TrashSection libraryId={libraryId} canWrite={canWrite} />
          )}
          {tab === 'shares' && canSeeGrants && (
            <section aria-label={t('share.adminTitle')} className="flex flex-col gap-2">
              <p className="text-sm text-muted">{t('share.adminIntro')}</p>
              {/* Même composant que « Mes partages » : la question posée est la même,
                  seule la source des données et la colonne d'auteur diffèrent. */}
              <SharesList load={listAllShares} showAuthor />
            </section>
          )}
          {isSuperadmin && tab === 'sessionLinks' && <SessionLinksSection />}
          {isSuperadmin && tab === 'apiKeys' && <ApiKeysSection />}
          {isSuperadmin && tab === 'logs' && <RequestLogsSection />}
          {isSuperadmin && tab === 'audit' && <AuditLogSection />}
          {isSuperadmin && tab === 'llm' && <LlmConfigPage libraryId={libraryId ?? LIBRARY_ID} />}
          {isSuperadmin && tab === 'aitrace' && <AiTracePage libraryId={libraryId ?? LIBRARY_ID} />}
          {isSuperadmin && tab === 'reset' && (
            <MaintenanceSection libraryId={libraryId ?? LIBRARY_ID} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Onglet accessible (bouton `role="tab"`). */
function TabButton({
  active,
  onClick,
  labelKey,
  panelId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  labelKey: MessageKey;
  panelId: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls={panelId}
      tabIndex={rovingTabIndex(active)}
      onClick={onClick}
      className={`-mb-px flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
        active ? 'border-brand text-fg' : 'border-transparent text-muted hover:text-fg'
      }`}
    >
      {children}
      {t(labelKey)}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section Général (superadmin, LOT 3) — nom du site
// ---------------------------------------------------------------------------

/**
 * Réglage « Général » : nom du site (LOT 3). Préremplit depuis le contexte global
 * (`useAppConfig().appName`, avec repli sur `useHealth().name` tant que le contexte est
 * au défaut), enregistre via `PATCH /api/app-settings`, puis met à jour le contexte →
 * le pied de page « à-propos » suit immédiatement. Le titre d'onglet est piloté par
 * `/api/health` (cf. `useDocumentTitle`) et se rafraîchit au prochain chargement.
 */
function GeneralSettingsSection(): React.JSX.Element {
  const { t } = useI18n();
  const {
    appName,
    setAppName,
    smallImageUpscalePercent,
    setSmallImageUpscalePercent,
    updatedAt,
    setUpdatedAt,
  } = useAppConfig();
  const { health } = useHealth();
  // Source du préremplissage : le contexte fait foi ; tant qu'il est au défaut, on
  // accepte le nom renvoyé par `/api/health` (même valeur DB, disponible pré-provider).
  const resolved = appName !== DEFAULT_APP_NAME ? appName : (health?.name ?? appName);
  const [value, setValue] = useState(resolved);
  const [busy, setBusy] = useState(false);
  // `conflict` = 409 STALE_UPDATE (audit A6) : un autre éditeur a enregistré entre-temps.
  // Message ACTIONNABLE (« rechargez ») et non une erreur générique.
  const [status, setStatus] = useState<'idle' | 'saved' | 'conflict' | 'error'>('idle');

  // Passerelle d'authentification (ADR-0008). Préremplie depuis `/api/health` (même
  // colonne DB que `/api/app-config`, mais health est déjà chargé ici) ; champ VIDE =
  // aucune passerelle. Le PATCH renvoie l'état enregistré → on réaligne le champ dessus.
  const gatewayLoaded = health?.accessGatewayUrl ?? '';
  const [gatewayValue, setGatewayValue] = useState(gatewayLoaded);
  const [gatewayBusy, setGatewayBusy] = useState(false);
  const [gatewayStatus, setGatewayStatus] = useState<
    'idle' | 'saved' | 'invalid' | 'conflict' | 'error'
  >('idle');

  // Agrandissement des petites photos (visionneuse). Champ NOMBRE (entier %) prérempli
  // depuis le contexte global ; validé/borné à l'enregistrement (le serveur re-borne).
  const [upscaleValue, setUpscaleValue] = useState(String(smallImageUpscalePercent));
  const [upscaleBusy, setUpscaleBusy] = useState(false);
  const [upscaleStatus, setUpscaleStatus] = useState<
    'idle' | 'saved' | 'invalid' | 'conflict' | 'error'
  >('idle');

  // Resynchronise le champ quand la valeur résolue arrive/évolue (chargement initial ;
  // après un enregistrement réussi qui met à jour le contexte).
  useEffect(() => {
    setValue(resolved);
  }, [resolved]);

  useEffect(() => {
    setGatewayValue(gatewayLoaded);
  }, [gatewayLoaded]);

  // Resynchronise le champ d'agrandissement quand la valeur du contexte arrive/évolue.
  useEffect(() => {
    setUpscaleValue(String(smallImageUpscalePercent));
  }, [smallImageUpscalePercent]);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    setStatus('idle');
    try {
      const config = await updateAppName(trimmed, updatedAt);
      setAppName(config.appName);
      setValue(config.appName);
      setUpdatedAt(config.updatedAt);
      setStatus('saved');
    } catch (err) {
      setStatus(isStaleUpdate(err) ? 'conflict' : 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitUpscale = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (upscaleBusy) return;
    // Validation locale (miroir des bornes serveur) : entier dans l'intervalle autorisé.
    const parsed = Number(upscaleValue);
    if (
      !Number.isInteger(parsed) ||
      parsed < SMALL_IMAGE_UPSCALE_MIN_PERCENT ||
      parsed > SMALL_IMAGE_UPSCALE_MAX_PERCENT
    ) {
      setUpscaleStatus('invalid');
      return;
    }
    setUpscaleBusy(true);
    setUpscaleStatus('idle');
    try {
      const config = await updateSmallImageUpscalePercent(parsed, updatedAt);
      setSmallImageUpscalePercent(config.smallImageUpscalePercent);
      setUpscaleValue(String(config.smallImageUpscalePercent));
      setUpdatedAt(config.updatedAt);
      setUpscaleStatus('saved');
    } catch (err) {
      setUpscaleStatus(isStaleUpdate(err) ? 'conflict' : 'error');
    } finally {
      setUpscaleBusy(false);
    }
  };

  const submitGateway = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (gatewayBusy) return;
    const trimmed = gatewayValue.trim();
    setGatewayBusy(true);
    setGatewayStatus('idle');
    try {
      // Champ vide → `null` = DÉSACTIVER la passerelle (c'est la façon prévue de la couper).
      const config = await updateAccessGatewayUrl(trimmed.length === 0 ? null : trimmed, updatedAt);
      setGatewayValue(config.accessGatewayUrl ?? '');
      setUpdatedAt(config.updatedAt);
      setGatewayStatus('saved');
    } catch (err) {
      // 400 VALIDATION = URL refusée (http/https uniquement) → message ACTIONNABLE,
      // distinct d'une panne réseau. 409 STALE_UPDATE = conflit de version (audit A6).
      const invalid = err instanceof AppConfigApiError && err.status === 400;
      setGatewayStatus(isStaleUpdate(err) ? 'conflict' : invalid ? 'invalid' : 'error');
    } finally {
      setGatewayBusy(false);
    }
  };

  return (
    <section
      id="admin-general"
      role="tabpanel"
      aria-labelledby="admin-general-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-general-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.general.title')}
        </h3>
        <p className="text-sm text-muted">{t('admin.general.intro')}</p>
      </div>

      <form
        onSubmit={(e) => void submit(e)}
        className="flex flex-col gap-4 rounded-lg border border-border p-4"
      >
        <Field id="general-app-name" label={t('admin.general.appNameLabel')}>
          <input
            id="general-app-name"
            type="text"
            required
            maxLength={120}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setStatus('idle');
            }}
            className={inputClass}
          />
        </Field>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={busy || value.trim().length === 0}
            // Deux « Enregistrer » vivent dans cette section : le nom accessible les
            // distingue (il CONTIENT le texte visible — WCAG 2.5.3 « Label in Name »).
            aria-label={t('admin.general.saveAppNameAria')}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {busy ? t('admin.general.saving') : t('admin.general.save')}
          </button>
          {status === 'saved' && (
            <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {t('admin.general.saved')}
            </p>
          )}
          {status === 'conflict' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-warning-text">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('admin.general.conflict')}
            </p>
          )}
          {status === 'error' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('admin.general.error')}
            </p>
          )}
        </div>
      </form>

      {/* Agrandissement des petites photos (visionneuse) — formulaire distinct : autre
          réglage, autre enregistrement. Placé juste sous le nom du site. */}
      <form
        onSubmit={(e) => void submitUpscale(e)}
        className="flex flex-col gap-4 rounded-lg border border-border p-4"
      >
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-fg">{t('admin.general.upscaleTitle')}</h4>
          <p className="text-sm text-muted">{t('admin.general.upscaleIntro')}</p>
        </div>

        <Field id="general-upscale" label={t('admin.general.upscaleLabel')}>
          <input
            id="general-upscale"
            type="number"
            inputMode="numeric"
            // Pas de `required` HTML : la validation (entier borné) est portée par
            // `submitUpscale` → message ACTIONNABLE unique, même si le champ est vidé.
            min={SMALL_IMAGE_UPSCALE_MIN_PERCENT}
            max={SMALL_IMAGE_UPSCALE_MAX_PERCENT}
            step={10}
            value={upscaleValue}
            onChange={(e) => {
              setUpscaleValue(e.target.value);
              setUpscaleStatus('idle');
            }}
            aria-describedby="general-upscale-help"
            className={inputClass}
          />
        </Field>
        <p id="general-upscale-help" className="text-xs text-muted">
          {interpolate(t('admin.general.upscaleHelp'), {
            min: SMALL_IMAGE_UPSCALE_MIN_PERCENT,
            max: SMALL_IMAGE_UPSCALE_MAX_PERCENT,
          })}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={upscaleBusy}
            aria-label={t('admin.general.saveUpscaleAria')}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {upscaleBusy ? t('admin.general.saving') : t('admin.general.save')}
          </button>
          {upscaleStatus === 'saved' && (
            <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {t('admin.general.upscaleSaved')}
            </p>
          )}
          {upscaleStatus === 'invalid' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {interpolate(t('admin.general.upscaleInvalid'), {
                min: SMALL_IMAGE_UPSCALE_MIN_PERCENT,
                max: SMALL_IMAGE_UPSCALE_MAX_PERCENT,
              })}
            </p>
          )}
          {upscaleStatus === 'conflict' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-warning-text">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('admin.general.conflict')}
            </p>
          )}
          {upscaleStatus === 'error' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('admin.general.error')}
            </p>
          )}
        </div>
      </form>

      {/* Passerelle d'authentification (ADR-0008) — formulaire distinct : autre sujet,
          autre enregistrement, et une explication qui a besoin de place. */}
      <form
        onSubmit={(e) => void submitGateway(e)}
        className="flex flex-col gap-4 rounded-lg border border-border p-4"
      >
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-fg">{t('admin.general.gatewayTitle')}</h4>
          <p className="text-sm text-muted">{t('admin.general.gatewayIntro')}</p>
        </div>

        <Field id="general-access-gateway" label={t('admin.general.gatewayLabel')}>
          <input
            id="general-access-gateway"
            type="url"
            inputMode="url"
            maxLength={ACCESS_GATEWAY_URL_MAX_LENGTH}
            placeholder={t('admin.general.gatewayPlaceholder')}
            aria-describedby="general-access-gateway-help"
            value={gatewayValue}
            onChange={(e) => {
              setGatewayValue(e.target.value);
              setGatewayStatus('idle');
            }}
            className={inputClass}
          />
        </Field>

        <div id="general-access-gateway-help" className="flex flex-col gap-2 text-sm text-muted">
          <p>{t('admin.general.gatewayHowTitle')}</p>
          <ol className="ml-4 flex list-decimal flex-col gap-1">
            <li>{t('admin.general.gatewayStep1')}</li>
            <li>{t('admin.general.gatewayStep2')}</li>
            <li>{t('admin.general.gatewayStep3')}</li>
          </ol>
          <p className="flex items-start gap-1.5 text-warning-text">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t('admin.general.gatewayWarning')}</span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={gatewayBusy}
            aria-label={t('admin.general.saveGatewayAria')}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {gatewayBusy ? t('admin.general.saving') : t('admin.general.save')}
          </button>
          {gatewayStatus === 'saved' && (
            <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {gatewayValue.trim().length === 0
                ? t('admin.general.gatewayDisabled')
                : t('admin.general.gatewaySaved')}
            </p>
          )}
          {gatewayStatus === 'invalid' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('admin.general.gatewayInvalid')}
            </p>
          )}
          {gatewayStatus === 'conflict' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-warning-text">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('admin.general.conflict')}
            </p>
          )}
          {gatewayStatus === 'error' && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('admin.general.error')}
            </p>
          )}
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section Comptes (superadmin)
// ---------------------------------------------------------------------------

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

function UsersSection(): React.JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetchUsers(controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        setUsers(list);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        void err;
      });
    return () => controller.abort();
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const upsert = useCallback((u: AdminUser) => {
    setUsers((prev) => {
      const idx = prev.findIndex((p) => p.id === u.id);
      if (idx === -1) return [...prev, u];
      const next = prev.slice();
      next[idx] = u;
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setUsers((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return (
    <section
      id="admin-users"
      role="tabpanel"
      aria-labelledby="admin-users-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-users-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <UsersIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.usersTitle')}
        </h3>
        <p className="text-sm text-muted">{t('admin.usersHint')}</p>
      </div>

      {status === 'error' ? (
        <LoadError onRetry={reload} />
      ) : status === 'ready' ? (
        <UsersTable
          users={users}
          currentUserId={currentUserId}
          onUpdated={upsert}
          onDeleted={remove}
        />
      ) : (
        <p role="status" className="py-8 text-center text-muted">
          {t('admin.loading')}
        </p>
      )}

      <CreateUserForm onCreated={upsert} />
    </section>
  );
}

function UsersTable({
  users,
  currentUserId,
  onUpdated,
  onDeleted,
}: {
  users: readonly AdminUser[];
  currentUserId: string | null;
  onUpdated: (u: AdminUser) => void;
  onDeleted: (id: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  if (users.length === 0) {
    return <p className="py-6 text-center text-muted">{t('admin.usersEmpty')}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-start text-xs font-medium text-muted">
            <th scope="col" className="p-2 text-start">
              {t('admin.colEmail')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.colName')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.colRole')}
            </th>
            <th scope="col" className="p-2 text-end">
              {t('admin.colActions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              isSelf={u.id === currentUserId}
              onUpdated={onUpdated}
              onDeleted={onDeleted}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({
  user,
  isSelf,
  onUpdated,
  onDeleted,
}: {
  user: AdminUser;
  /** Ligne du compte ACTUELLEMENT connecté : le bouton « Supprimer » est masqué. */
  isSelf: boolean;
  onUpdated: (u: AdminUser) => void;
  onDeleted: (id: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Confirmation de suppression via NOTRE modale (jamais `window.confirm`).
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Réinitialisation du mot de passe (superadmin) : champ révélable inline.
  const [resetOpen, setResetOpen] = useState(false);
  // Sessions actives (audit B2) : panneau dépliable, même mécanique que la
  // réinitialisation. Monté À LA DEMANDE — on ne veut pas N requêtes de sessions au
  // chargement de la liste des comptes.
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState(false);

  const changeRole = async (role: UserRole): Promise<void> => {
    if (role === user.role || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateUser(user.id, { role });
      onUpdated(updated);
    } catch (err: unknown) {
      setError(isConflict(err) ? t('admin.conflictLastAdmin') : t('admin.genericError'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setConfirmOpen(false);
    try {
      await deleteUser(user.id);
      onDeleted(user.id);
    } catch (err: unknown) {
      // Message dédié si une COURSE renvoie 409 auto-suppression (bouton masqué,
      // mais l'API garde la vérité) — sinon dernier superadmin / erreur générique.
      const message = isCannotDeleteSelf(err)
        ? t('admin.cannotDeleteSelf')
        : isConflict(err)
          ? t('admin.conflictLastAdmin')
          : t('admin.genericError');
      setError(message);
    } finally {
      // `finally` et NON le seul `catch` (audit F9) : si le retrait de la ligne
      // n'a pas lieu côté parent, le bouton restait verrouillé à vie.
      setBusy(false);
    }
  };

  const onResetPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (resetBusy) return;
    setResetBusy(true);
    setResetError(null);
    setResetDone(false);
    try {
      const updated = await updateUser(user.id, { password: newPassword });
      onUpdated(updated);
      setResetDone(true);
      setNewPassword('');
    } catch {
      setResetError(t('admin.resetPasswordError'));
    } finally {
      setResetBusy(false);
    }
  };

  const toggleReset = (): void => {
    setResetError(null);
    setResetDone(false);
    setNewPassword('');
    setResetOpen((o) => !o);
  };

  const roleId = `role-${user.id}`;
  const errId = `err-${user.id}`;
  const resetPwId = `reset-pw-${user.id}`;
  return (
    <tr className="border-b border-border">
      <td className="p-2 align-top text-fg">
        <span className="break-all">{user.email}</span>
        {error && (
          <p id={errId} role="alert" className="mt-1 flex items-center gap-1 text-xs text-danger">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
      </td>
      <td className="p-2 align-top text-fg">{user.displayName}</td>
      <td className="p-2 align-top">
        <label htmlFor={roleId} className="sr-only">
          {interpolate(t('admin.editRole'), { name: user.displayName })}
        </label>
        <select
          id={roleId}
          value={user.role}
          disabled={busy}
          aria-invalid={error !== null}
          aria-describedby={error ? errId : undefined}
          onChange={(e) => void changeRole(e.target.value as UserRole)}
          className="h-9 rounded-md border border-border-strong bg-surface px-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {t(ROLE_LABEL[r])}
            </option>
          ))}
        </select>
      </td>
      <td className="p-2 align-top text-end">
        {isSelf ? (
          // Un compte ne peut ni se supprimer ni réinitialiser son mot de passe
          // ici : le superadmin change le sien via Réglages. Bouton masqué + note.
          <span className="inline-flex items-center gap-1 text-xs text-muted">
            {t('admin.deleteSelfDisabled')}
          </span>
        ) : (
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-wrap items-center justify-end gap-1">
              <button
                type="button"
                aria-expanded={sessionsOpen}
                onClick={() => setSessionsOpen((o) => !o)}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                <ComputerDesktopIcon className="h-4 w-4" aria-hidden="true" />
                {t('admin.sessions.button')}
              </button>
              <button
                type="button"
                aria-expanded={resetOpen}
                onClick={toggleReset}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                <KeyIcon className="h-4 w-4" aria-hidden="true" />
                {t('admin.resetPassword')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmOpen(true)}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-danger hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                {t('admin.delete')}
              </button>
            </div>
            {sessionsOpen && <UserSessionsPanel userId={user.id} />}
            {resetOpen && (
              <form
                onSubmit={(e) => void onResetPassword(e)}
                className="flex w-full max-w-xs flex-col gap-2 rounded-md border border-border p-3 text-start"
              >
                <label htmlFor={resetPwId} className="text-xs font-medium text-fg">
                  {interpolate(t('admin.resetPasswordFor'), { name: user.displayName })}
                </label>
                <PasswordInput
                  id={resetPwId}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder={t('admin.resetPasswordPlaceholder')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={inputClass}
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setResetOpen(false)}
                    className="rounded-md px-2 py-1 text-sm text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {t('admin.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={resetBusy}
                    className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {resetBusy ? t('admin.resettingPassword') : t('admin.resetPasswordSubmit')}
                  </button>
                </div>
                {resetDone && (
                  <p role="status" className="inline-flex items-center gap-1 text-xs text-fg">
                    <CheckCircleIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                    {t('admin.resetPasswordDone')}
                  </p>
                )}
                {resetError && (
                  <p role="alert" className="inline-flex items-center gap-1 text-xs text-danger">
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {resetError}
                  </p>
                )}
              </form>
            )}
          </div>
        )}
        {/* Confirmation (NOTRE modale, jamais `window.confirm`) — Portal hors du `<tr>`. */}
        {confirmOpen && (
          <Portal>
            <ConfirmModal
              title={t('admin.confirmDeleteTitle')}
              confirmLabel={t('admin.delete')}
              cancelLabel={t('admin.cancel')}
              busyLabel={t('admin.deleting')}
              busy={busy}
              danger
              onConfirm={() => void onDelete()}
              onClose={() => setConfirmOpen(false)}
            >
              <p>{interpolate(t('admin.confirmDelete'), { name: user.displayName })}</p>
            </ConfirmModal>
          </Portal>
        )}
      </td>
    </tr>
  );
}

function CreateUserForm({ onCreated }: { onCreated: (u: AdminUser) => void }): React.JSX.Element {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [password, setPassword] = useState('');
  const [grantFullRead, setGrantFullRead] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // La case « accès lecture racine » ne concerne qu'un compte LECTEUR (`user`) :
  // un superadmin voit déjà tout et un admin a l'écriture sur toute la bibliothèque.
  const showGrantFullRead = role === 'user';

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setDone(false);
    const input: CreateUserRequest = {
      email,
      displayName,
      role,
      password,
      grantFullRead: showGrantFullRead ? grantFullRead : false,
    };
    try {
      const created = await createUser(input);
      onCreated(created);
      setDone(true);
      setEmail('');
      setDisplayName('');
      setRole('user');
      setPassword('');
      setGrantFullRead(true);
    } catch (err: unknown) {
      // Message CIBLÉ si le conflit porte sur l'e-mail, avec repli générique pour
      // un 409 sans code machine-lisible reconnu.
      const kind = createUserConflictKind(err);
      setError(
        kind === 'email'
          ? t('admin.conflictEmail')
          : kind === 'other'
            ? t('admin.conflictUser')
            : t('admin.genericError'),
      );
    } finally {
      setBusy(false);
    }
  };

  const errId = 'create-user-error';
  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      <h4 className="text-sm font-semibold text-fg">{t('admin.createUserTitle')}</h4>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field id="cu-email" label={t('admin.fieldEmail')}>
          <input
            id="cu-email"
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={error !== null}
            aria-describedby={error ? errId : undefined}
            className={inputClass}
          />
        </Field>
        <Field id="cu-name" label={t('admin.fieldName')}>
          <input
            id="cu-name"
            type="text"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field id="cu-role" label={t('admin.fieldRole')}>
          <select
            id="cu-role"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className={inputClass}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(ROLE_LABEL[r])}
              </option>
            ))}
          </select>
        </Field>
        <Field id="cu-password" label={t('admin.fieldPassword')}>
          <PasswordInput
            id="cu-password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      {showGrantFullRead && (
        <label className="flex items-start gap-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={grantFullRead}
            onChange={(e) => setGrantFullRead(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          />
          <span>
            {t('admin.grantFullRead')}
            <span className="block text-xs text-muted">{t('admin.grantFullReadHint')}</span>
          </span>
        </label>
      )}

      {/* Un admin a l'écriture sur toute la bibliothèque : ni case ni grant à définir. */}
      {role === 'admin' && (
        <p className="flex items-start gap-2 text-sm text-muted">
          <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          {t('admin.adminWriteHint')}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {busy ? t('admin.creating') : t('admin.create')}
        </button>
        {done && (
          <p role="status" className="text-sm text-fg">
            {t('admin.userCreated')}
          </p>
        )}
        {error && (
          <p id={errId} role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Section Accès (grants)
// ---------------------------------------------------------------------------

function GrantsSection({
  libraryId,
  isSuperadmin,
}: {
  libraryId: string | null;
  isSuperadmin: boolean;
}): React.JSX.Element {
  const { t } = useI18n();
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [grants, setGrants] = useState<Grant[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!libraryId) {
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    fetchGrants(libraryId, controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        setGrants(list);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        void err;
      });
    return () => controller.abort();
  }, [libraryId, reloadKey]);

  // Alimente la liste déroulante des bénéficiaires (superadmin uniquement).
  useEffect(() => {
    if (!isSuperadmin) return;
    const controller = new AbortController();
    fetchUsers(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setUsers(list);
      })
      .catch(() => {
        // Non bloquant : on retombe sur la saisie d'un identifiant.
      });
    return () => controller.abort();
  }, [isSuperadmin]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const addGrant = useCallback((g: Grant) => {
    setGrants((prev) => [...prev, g]);
  }, []);

  const removeGrant = useCallback((id: string) => {
    setGrants((prev) => prev.filter((g) => g.id !== id));
  }, []);

  return (
    <section
      id="admin-grants"
      role="tabpanel"
      aria-labelledby="admin-grants-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-grants-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <KeyIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.grantsTitle')}
        </h3>
        <p className="text-sm text-muted">{t('admin.grantsHint')}</p>
        <p className="inline-flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-fg">
          <KeyIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {t('admin.grantsReadOnly')}
        </p>
      </div>

      {!libraryId ? (
        <p className="py-6 text-center text-muted">{t('admin.noLibrarySelected')}</p>
      ) : status === 'error' ? (
        <LoadError onRetry={reload} />
      ) : status === 'ready' ? (
        <>
          <GrantsTable grants={grants} libraryId={libraryId} onRevoked={removeGrant} />
          <CreateGrantForm libraryId={libraryId} users={users} onCreated={addGrant} />
        </>
      ) : (
        <p role="status" className="py-8 text-center text-muted">
          {t('admin.loading')}
        </p>
      )}
    </section>
  );
}

function GrantsTable({
  grants,
  libraryId,
  onRevoked,
}: {
  grants: readonly Grant[];
  libraryId: string;
  onRevoked: (id: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  if (grants.length === 0) {
    return <p className="py-6 text-center text-muted">{t('admin.grantsEmpty')}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[30rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-xs font-medium text-muted">
            <th scope="col" className="p-2 text-start">
              {t('admin.colBeneficiary')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.colSubtree')}
            </th>
            <th scope="col" className="p-2 text-end">
              {t('admin.colActions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {grants.map((g) => (
            <GrantRow key={g.id} grant={g} libraryId={libraryId} onRevoked={onRevoked} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrantRow({
  grant,
  libraryId,
  onRevoked,
}: {
  grant: Grant;
  libraryId: string;
  onRevoked: (id: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Confirmation de révocation via NOTRE modale (jamais `window.confirm`).
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onRevoke = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setConfirmOpen(false);
    try {
      await deleteGrant(libraryId, grant.id);
      onRevoked(grant.id);
    } catch {
      setError(t('admin.genericError'));
    } finally {
      // `finally` et NON le seul `catch` (audit F9) : si le retrait de la ligne
      // n'a pas lieu côté parent, le bouton restait verrouillé à vie.
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-border">
      <td className="p-2 align-top text-fg">
        <span className="font-medium">{grant.displayName}</span>
        <span className="block break-all text-xs text-muted">{grant.email}</span>
        {error && (
          <p role="alert" className="mt-1 flex items-center gap-1 text-xs text-danger">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
      </td>
      <td className="p-2 align-top text-muted">
        {grant.subtreePath === '' ? (
          t('admin.subtreeRoot')
        ) : (
          <span className="break-all">{grant.subtreePath}</span>
        )}
      </td>
      <td className="p-2 align-top text-end">
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirmOpen(true)}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-danger hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
        >
          <TrashIcon className="h-4 w-4" aria-hidden="true" />
          {t('admin.revoke')}
        </button>
        {/* Confirmation (NOTRE modale, jamais `window.confirm`) — Portal hors du `<tr>`. */}
        {confirmOpen && (
          <Portal>
            <ConfirmModal
              title={t('admin.confirmRevokeTitle')}
              confirmLabel={t('admin.revoke')}
              cancelLabel={t('admin.cancel')}
              busyLabel={t('admin.revoking')}
              busy={busy}
              danger
              onConfirm={() => void onRevoke()}
              onClose={() => setConfirmOpen(false)}
            >
              <p>{interpolate(t('admin.confirmRevoke'), { name: grant.displayName })}</p>
            </ConfirmModal>
          </Portal>
        )}
      </td>
    </tr>
  );
}

function CreateGrantForm({
  libraryId,
  users,
  onCreated,
}: {
  libraryId: string;
  users: readonly AdminUser[];
  onCreated: (g: Grant) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [userId, setUserId] = useState('');
  const [subtreePath, setSubtreePath] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Liste déroulante quand on connaît les utilisateurs (superadmin), sinon champ id.
  const hasUserList = users.length > 0;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      const created = await createGrant(libraryId, { userId, subtreePath });
      onCreated(created);
      setDone(true);
      setUserId('');
      setSubtreePath('');
    } catch (err: unknown) {
      if (isConflict(err)) setError(t('admin.conflictGrant'));
      else if (err instanceof AdminApiError && err.status === 404)
        setError(t('admin.notFoundUser'));
      else setError(t('admin.genericError'));
    } finally {
      setBusy(false);
    }
  };

  const errId = 'create-grant-error';
  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      <h4 className="text-sm font-semibold text-fg">{t('admin.createGrantTitle')}</h4>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          id="cg-user"
          label={hasUserList ? t('admin.fieldBeneficiary') : t('admin.fieldUserId')}
        >
          {hasUserList ? (
            <select
              id="cg-user"
              required
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={error ? errId : undefined}
              className={inputClass}
            >
              <option value="">{t('admin.chooseBeneficiary')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName} — {u.email}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="cg-user"
              type="text"
              required
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={error ? errId : undefined}
              className={inputClass}
            />
          )}
        </Field>
        <Field id="cg-subtree" label={t('admin.fieldSubtree')}>
          <input
            id="cg-subtree"
            type="text"
            value={subtreePath}
            placeholder={t('admin.subtreePlaceholder')}
            onChange={(e) => setSubtreePath(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {busy ? t('admin.granting') : t('admin.grant')}
        </button>
        {done && (
          <p role="status" className="text-sm text-fg">
            {t('admin.grantCreated')}
          </p>
        )}
        {error && (
          <p id={errId} role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Section Journaux (superadmin) — journalisation de toutes les requêtes API
// ---------------------------------------------------------------------------

const LOGS_PAGE_SIZE = REQUEST_LOG_DEFAULT_LIMIT;

/** Options du filtre « Méthode » (verbe HTTP) des journaux de requêtes. */
const REQ_METHOD_OPTIONS: readonly FilterOption<RequestLogMethod>[] = [
  { value: '', labelKey: 'admin.logs.methodAll' },
  { value: 'GET', labelKey: 'admin.logs.methodGet' },
  { value: 'POST', labelKey: 'admin.logs.methodPost' },
  { value: 'PATCH', labelKey: 'admin.logs.methodPatch' },
  { value: 'DELETE', labelKey: 'admin.logs.methodDelete' },
];

/** Options du filtre « Statut » (classe HTTP) des journaux de requêtes. */
const REQ_STATUS_OPTIONS: readonly FilterOption<RequestLogStatusClass>[] = [
  { value: '', labelKey: 'admin.logs.statusAll' },
  { value: '2xx', labelKey: 'admin.logs.status2xx' },
  { value: '3xx', labelKey: 'admin.logs.status3xx' },
  { value: '4xx', labelKey: 'admin.logs.status4xx' },
  { value: '5xx', labelKey: 'admin.logs.status5xx' },
];

function RequestLogsSection(): React.JSX.Element {
  const { t, locale } = useI18n();
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);

  // Filtres : Utilisateur / Statut (classe HTTP) / Méthode (verbe HTTP).
  const [userId, setUserId] = useState('');
  const [statusClass, setStatusClass] = useState<RequestLogStatusClass | ''>('');
  const [method, setMethod] = useState<RequestLogMethod | ''>('');

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetchRequestLogs(
      LOGS_PAGE_SIZE,
      offset,
      {
        ...(userId !== '' ? { userId } : {}),
        ...(statusClass !== '' ? { statusClass } : {}),
        ...(method !== '' ? { method } : {}),
      },
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) return;
        setLogs(page.logs);
        setTotal(page.total);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        void err;
      });
    return () => controller.abort();
  }, [offset, reloadKey, userId, statusClass, method]);

  // Alimente le filtre « Utilisateur » (liste des comptes, best-effort).
  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setUsers(list);
      })
      .catch(() => {
        // Non bloquant.
      });
    return () => controller.abort();
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const onFilterChange =
    <T,>(setter: (v: T) => void) =>
    (v: T): void => {
      setter(v);
      setOffset(0);
    };

  const onPurged = useCallback(() => {
    setOffset(0);
    reload();
  }, [reload]);

  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + logs.length, total);
  const canPrev = offset > 0;
  const canNext = offset + LOGS_PAGE_SIZE < total;

  const userOptions = users.map((u) => ({ value: u.id, label: `${u.displayName} — ${u.email}` }));

  return (
    <section
      id="admin-logs"
      role="tabpanel"
      aria-labelledby="admin-logs-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-logs-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <ClipboardDocumentListIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.logs.title')}
        </h3>
        <p className="text-sm text-muted">{t('admin.logs.intro')}</p>
        <p className="inline-flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-fg">
          <ClipboardDocumentListIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {t('admin.logs.retention')}
        </p>
      </div>

      {/* Barre de 3 filtres : Utilisateur / Statut (classe HTTP) / Méthode (verbe). */}
      <div className="flex flex-wrap items-end gap-3">
        <DynamicFilterSelect
          id="logs-filter-user"
          labelKey="admin.logs.filterUser"
          allLabelKey="admin.logs.userAll"
          value={userId}
          options={userOptions}
          onChange={onFilterChange(setUserId)}
        />
        <FilterSelect<RequestLogStatusClass>
          id="logs-filter-status"
          labelKey="admin.logs.filterStatus"
          value={statusClass}
          options={REQ_STATUS_OPTIONS}
          onChange={onFilterChange<RequestLogStatusClass | ''>(setStatusClass)}
        />
        <FilterSelect<RequestLogMethod>
          id="logs-filter-method"
          labelKey="admin.logs.filterMethod"
          value={method}
          options={REQ_METHOD_OPTIONS}
          onChange={onFilterChange<RequestLogMethod | ''>(setMethod)}
        />
      </div>

      {/* Purge unifiée (composant réutilisable). */}
      <PurgeByDateControl onPurge={purgeRequestLogs} onPurged={onPurged} />

      {status === 'error' ? (
        <LoadError onRetry={reload} />
      ) : status === 'ready' ? (
        <>
          <RequestLogsTable logs={logs} locale={locale} />
          {total > 0 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted" role="status">
                {interpolate(
                  t('admin.logs.range'),
                  { from: rangeFrom, to: rangeTo, total },
                  locale,
                )}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={() => setOffset((o) => Math.max(0, o - LOGS_PAGE_SIZE))}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.logs.previous')}
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => setOffset((o) => o + LOGS_PAGE_SIZE)}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.logs.next')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p role="status" className="py-8 text-center text-muted">
          {t('admin.loading')}
        </p>
      )}
    </section>
  );
}

function RequestLogsTable({
  logs,
  locale,
}: {
  logs: readonly RequestLog[];
  locale: Locale;
}): React.JSX.Element {
  const { t } = useI18n();
  if (logs.length === 0) {
    return <p className="py-6 text-center text-muted">{t('admin.logs.empty')}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[48rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-start text-xs font-medium text-muted">
            <th scope="col" className="p-2 text-start">
              {t('admin.logs.colTimestamp')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.logs.colMethod')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.logs.colPath')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.logs.colStatus')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.logs.colUser')}
            </th>
            <th scope="col" className="p-2 text-end">
              {t('admin.logs.colDuration')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.logs.colError')}
            </th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <RequestLogRow key={log.id} log={log} locale={locale} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Icône + libellé de classe de statut (jamais la couleur seule). */
function StatusBadge({ status }: { status: number }): React.JSX.Element {
  const { t } = useI18n();
  let icon: React.ReactNode;
  let labelKey: MessageKey;
  if (status >= 500) {
    icon = <XCircleIcon className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />;
    labelKey = 'admin.logs.statusServerError';
  } else if (status >= 400) {
    icon = <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />;
    labelKey = 'admin.logs.statusClientError';
  } else if (status >= 300) {
    icon = <ArrowPathIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />;
    labelKey = 'admin.logs.statusRedirect';
  } else {
    icon = <CheckCircleIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />;
    labelKey = 'admin.logs.statusSuccess';
  }
  return (
    <span className="inline-flex items-center gap-1 text-fg">
      {icon}
      <span className="font-medium tabular-nums">{status}</span>
      <span className="sr-only">{t(labelKey)}</span>
    </span>
  );
}

function RequestLogRow({ log, locale }: { log: RequestLog; locale: Locale }): React.JSX.Element {
  const { t } = useI18n();
  const when = formatDateTime(log.ts, locale) ?? log.ts;
  return (
    <tr className="border-b border-border">
      <td className="p-2 align-top text-muted whitespace-nowrap">{when}</td>
      <td className="p-2 align-top text-fg font-medium">{log.method}</td>
      <td className="p-2 align-top text-fg">
        <span className="break-all">{log.route ?? log.path}</span>
      </td>
      <td className="p-2 align-top">
        <StatusBadge status={log.status} />
      </td>
      <td className="p-2 align-top text-muted">
        <span className="break-all">{log.userEmail ?? t('admin.logs.noUser')}</span>
      </td>
      <td className="p-2 align-top text-end text-muted tabular-nums whitespace-nowrap">
        {interpolate(t('admin.logs.duration'), { ms: log.durationMs })}
      </td>
      <td className="p-2 align-top">
        {log.error ? (
          <span className="inline-flex items-start gap-1 text-danger">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="break-all">{log.error}</span>
          </span>
        ) : (
          <span className="text-muted">{t('admin.logs.noUser')}</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Section Maintenance (superadmin) — réinitialisation « repartir de zéro »
// ---------------------------------------------------------------------------

function MaintenanceSection({ libraryId }: { libraryId: string | null }): React.JSX.Element {
  const { t, locale } = useI18n();
  const [modalOpen, setModalOpen] = useState(false);
  const [lastResult, setLastResult] = useState<{ assets: number; thumbnails: number } | null>(null);

  return (
    <section
      id="admin-reset"
      role="tabpanel"
      aria-labelledby="admin-reset-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-reset-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <WrenchScrewdriverIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.reset.title')}
        </h3>
        <p className="text-sm text-muted">{t('admin.reset.intro')}</p>
      </div>

      {libraryId === null ? (
        <p className="py-6 text-center text-muted">{t('admin.noLibrarySelected')}</p>
      ) : (
        <div className="flex flex-col gap-4 rounded-lg border border-danger/40 p-4">
          {/* Ce qui sera supprimé / préservé (icône + texte, jamais la couleur seule). */}
          <p className="flex items-start gap-2 text-sm text-fg">
            <ExclamationTriangleIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-danger"
              aria-hidden="true"
            />
            {t('admin.reset.deletes')}
          </p>
          <p className="flex items-start gap-2 text-sm text-fg">
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
            {t('admin.reset.preserves')}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setLastResult(null);
                setModalOpen(true);
              }}
              className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
            >
              <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
              {t('admin.reset.button')}
            </button>
            {lastResult && (
              <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
                <CheckCircleIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                {interpolate(
                  t('admin.reset.done'),
                  {
                    assets: lastResult.assets,
                    thumbnails: lastResult.thumbnails,
                  },
                  locale,
                )}
              </p>
            )}
          </div>
        </div>
      )}

      {modalOpen && libraryId !== null && (
        <Portal>
          <ResetLibraryModal
            libraryId={libraryId}
            onClose={() => setModalOpen(false)}
            onDone={(res) =>
              setLastResult({ assets: res.deletedAssets, thumbnails: res.deletedThumbnails })
            }
          />
        </Portal>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Utilitaires de présentation
// ---------------------------------------------------------------------------

const inputClass =
  'h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

/** Champ de formulaire : label lié + slot de contrôle. */
function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {label}
      </label>
      {children}
    </div>
  );
}

/** État d'erreur de chargement (icône + texte + réessai). */
function LoadError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <ExclamationTriangleIcon className="h-8 w-8 text-danger" aria-hidden="true" />
      <p className="text-fg">{t('admin.errorTitle')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('admin.retry')}
      </button>
    </div>
  );
}
